import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Copy, Edit, Plus, Trash2 } from "lucide-react";
import React, { useEffect } from "react";
import type { Chapter, PlotCard } from "../types";

type ContextType = 'default' | 'chapter' | 'plotcard';

interface EnhancedContextMenuProps {
    children: React.ReactNode;
    // Default actions
    addPlotLine?: () => void;
    addChapter?: () => void;
    onItemDelete?: () => void;
    selectedItems?: PlotCard[];

    // Chapter actions
    onEditChapter?: (chapterId: number, data: { name: string; description: string; outline?: string }) => void;
    onDeleteChapter?: (chapterId: number) => void;
    currentChapter?: Chapter;

    // Plot card actions
    onEditCard?: (card: PlotCard) => void;
    onDeleteCard?: (card: PlotCard) => void;
    onCopyCard?: (card: PlotCard) => void;
    currentCard?: PlotCard;
}

export const EnhancedContextMenu: React.FC<EnhancedContextMenuProps> = ({
    children,
    addPlotLine,
    addChapter,
    onItemDelete,
    selectedItems = [],
    onEditChapter,
    onDeleteChapter,
    currentChapter,
    onEditCard,
    onDeleteCard,
    onCopyCard,
    currentCard
}) => {
    // Determine the context type based on what's currently selected
    const contextType: ContextType = currentCard
        ? 'plotcard'
        : currentChapter
            ? 'chapter'
            : 'default';

    // For debugging
    useEffect(() => {
        console.log('Context menu state:', {
            contextType,
            hasCurrentChapter: !!currentChapter,
            currentChapterId: currentChapter?.id,
            currentChapterName: currentChapter?.name,
            hasCurrentCard: !!currentCard,
            currentCardId: currentCard?.id,
            currentCardTitle: currentCard?.title,
            hasEditChapter: !!onEditChapter,
            hasDeleteChapter: !!onDeleteChapter
        });
    }, [contextType, currentChapter, currentCard, onEditChapter, onDeleteChapter]);

    return (
        <ContextMenu>
            <ContextMenuTrigger>
                <div>{children}</div>
            </ContextMenuTrigger>
            <ContextMenuContent className="w-64 rounded-xl border-border/60 shadow-xl bg-background/95 backdrop-blur-xl">
                {/* Default context menu items */}
                {contextType === 'default' && (
                    <>
                        {addPlotLine && (
                            <ContextMenuItem onClick={addPlotLine}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add Plot Line
                            </ContextMenuItem>
                        )}
                        {addChapter && (
                            <ContextMenuItem onClick={addChapter}>
                                <Plus className="mr-2 h-4 w-4" />
                                Create New Chapter
                            </ContextMenuItem>
                        )}
                        {selectedItems.length > 0 && onItemDelete && (
                            <>
                                <ContextMenuSeparator />
                                <ContextMenuItem onClick={onItemDelete} className="text-destructive">
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete Selected ({selectedItems.length})
                                </ContextMenuItem>
                            </>
                        )}
                    </>
                )}

                {/* Chapter context menu items */}
                {contextType === 'chapter' && currentChapter && (
                    <>
                        {addChapter && (
                            <ContextMenuItem onClick={() => addChapter()}>
                                <Plus className="mr-2 h-4 w-4" />
                                Create New Chapter
                            </ContextMenuItem>
                        )}

                        <ContextMenuSeparator />

                        {onEditChapter && (
                            <ContextMenuItem onClick={async () => {
                                console.log('Edit chapter clicked for:', currentChapter);
                                if (currentChapter) {
                                    // Load existing outline if available
                                    let existingOutline = '';
                                    try {
                                        const { getChapterOutline } = await import('@/services/supabase/chapter-outlines');
                                        const outline = await getChapterOutline(currentChapter.id);
                                        existingOutline = outline?.content || '';
                                    } catch (error) {
                                        console.warn('Could not load chapter outline:', error);
                                    }

                                    onEditChapter(currentChapter.id, {
                                        name: currentChapter.name,
                                        description: currentChapter.description || '',
                                        outline: existingOutline
                                    });
                                }
                            }}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Chapter "{currentChapter.name || currentChapter.title}"
                            </ContextMenuItem>
                        )}

                        {onDeleteChapter && (
                            <ContextMenuItem
                                onClick={() => {
                                    console.log('Delete chapter clicked for:', currentChapter);
                                    if (currentChapter) {
                                        onDeleteChapter(currentChapter.id);
                                    }
                                }}
                                className="text-destructive"
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Chapter "{currentChapter.name || currentChapter.title}"
                            </ContextMenuItem>
                        )}
                    </>
                )}

                {/* Plot card context menu items */}
                {contextType === 'plotcard' && currentCard && (
                    <>
                        {onEditCard && (
                            <ContextMenuItem onClick={() => onEditCard(currentCard)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Card "{currentCard.title}"
                            </ContextMenuItem>
                        )}

                        {onCopyCard && (
                            <ContextMenuItem onClick={() => onCopyCard(currentCard)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Duplicate Card
                            </ContextMenuItem>
                        )}

                        {onDeleteCard && (
                            <ContextMenuItem
                                onClick={() => onDeleteCard(currentCard)}
                                className="text-destructive"
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Card
                            </ContextMenuItem>
                        )}
                    </>
                )}
            </ContextMenuContent>
        </ContextMenu>
    );
};

export default EnhancedContextMenu;